interface IZipcodeFormControls {
    zipcodeConfirmButton: HTMLButtonElement | null;
    zipcodeErrorMessage: HTMLDivElement | null;
    zipcodeInput: HTMLInputElement | null;
    zipcodeValue: string;
}

/**
 * Enum for controlling visibility of error messages
 */
enum ErrorMessageVisibility {
    Hidden = 'hidden',
    Visible = 'visible'
}

/**
 * Class responsible for validating zipcode form inputs
 * and providing visual feedback on validation status
 */
export class ZipcodeFormValidator {
    private static readonly ZIPCODE_INPUT_ID: string = 'storesModalInput';
    private static readonly ZIPCODE_ERROR_MESSAGE_ID: string =
        'zipcodeErrorMessage';
    private static readonly ZIPCODE_CONFIRM_BUTTON_ID: string =
        'zipcodeConfirmButton';

    private set errorMessageVisibility(visibility: ErrorMessageVisibility) {
        const { zipcodeErrorMessage } = this.getControls();

        if (zipcodeErrorMessage) {
            zipcodeErrorMessage.style.visibility = visibility;
        } else {
            console.warn(
                `Zipcode error message element with ID "${ZipcodeFormValidator.ZIPCODE_ERROR_MESSAGE_ID}" not found`
            );
        }
    }

    /**
     * Get the HTMX library from the global window object
     */
    private get htmx(): any {
        const htmxLib = (window as any)['htmx'];
        if (!htmxLib) {
            console.warn('HTMX library not found on window object');
        }
        return htmxLib;
    }

    /**
     * Create a new ZipcodeFormValidator and set up event listeners
     */
    constructor() {
        const { zipcodeInput, zipcodeValue } = this.getControls();

        // Set up event listeners for validation
        if (zipcodeInput) {
            zipcodeInput.addEventListener(
                'keyup',
                this.zipCodeInputKeyUp.bind(this)
            );

            zipcodeInput.addEventListener(
                'keypress',
                this.zipCodeInputKeyPress.bind(this)
            );

            if (zipcodeValue !== '') {
                this.validateZipcode();
            }
        } else {
            console.warn(
                `Zipcode input element with ID "${ZipcodeFormValidator.ZIPCODE_INPUT_ID}" not found`
            );
        }
    }

    /**
     * Get references to form control elements
     * @returns Object containing references to zipcode form controls
     */
    private getControls(): IZipcodeFormControls {
        const zipcodeInput = document.getElementById(
            ZipcodeFormValidator.ZIPCODE_INPUT_ID
        ) as HTMLInputElement | null;

        return {
            zipcodeConfirmButton: document.getElementById(
                ZipcodeFormValidator.ZIPCODE_CONFIRM_BUTTON_ID
            ) as HTMLButtonElement | null,
            zipcodeErrorMessage: document.getElementById(
                ZipcodeFormValidator.ZIPCODE_ERROR_MESSAGE_ID
            ) as HTMLDivElement | null,
            zipcodeInput,
            zipcodeValue: zipcodeInput?.value ?? ''
        };
    }

    /**
     * Handle keypress events, preventing non-numeric inputs
     * @param event - Keyboard event
     */
    private zipCodeInputKeyPress(event: KeyboardEvent): void {
        if (!this.isValidKey(event.key)) {
            event.preventDefault();
        }
    }

    /**
     * Handle keyup events to validate zipcode as user types
     * @param event - Keyboard event
     */
    private zipCodeInputKeyUp(event: KeyboardEvent): void {
        if (this.isValidKey(event.key)) {
            this.validateZipcode();
        }
    }

    /**
     * Check if a keyboard key is valid for zipcode input
     * @param key - The key that was pressed
     * @returns True if the key is valid for zipcode input
     */
    private isValidKey(key: string): boolean {
        return (
            [
                'Backspace',
                'Delete',
                'Enter',
                'Tab',
                'ArrowLeft',
                'ArrowRight',
                '-'
            ].includes(key) || /^\d$/.test(key)
        );
    }

    /**
     * Check if user is still typing the +4 portion
     * Handles both hyphenated (12345-) and unhyphenated (123456) formats
     */
    private isTypingPlusFour(zipcode: string): boolean {
        // Hyphenated format: "12345-", "12345-1", "12345-12", "12345-123"
        if (/^\d{5}-\d{0,3}$/.test(zipcode)) {
            return true;
        }

        // Unhyphenated format: 6-8 digits (still typing the +4)
        if (/^\d{6,8}$/.test(zipcode)) {
            return true;
        }

        return false;
    }

    /**
     * Validate the zipcode input and update UI accordingly
     */
    validateZipcode(): void {
        const { zipcodeConfirmButton, zipcodeInput, zipcodeValue } =
            this.getControls();

        if (!zipcodeInput || !this.htmx) {
            return;
        }

        if (zipcodeValue.length < 5 || this.isTypingPlusFour(zipcodeValue)) {
            // Still typing - don't show validation yet
            this.htmx.removeClass(zipcodeInput, 'is-invalid');
            this.htmx.removeClass(zipcodeInput, 'is-valid');

            if (zipcodeConfirmButton) {
                zipcodeConfirmButton.disabled = true;
            }

            this.errorMessageVisibility = ErrorMessageVisibility.Hidden;
        } else if (this.isValidZipcodeFormat(zipcodeValue)) {
            // if it is 5 digits and valid format, then it is valid
            if (zipcodeConfirmButton) {
                zipcodeConfirmButton.disabled = false;
            }

            this.htmx.removeClass(zipcodeInput, 'is-invalid');
            this.htmx.addClass(zipcodeInput, 'is-valid');

            this.errorMessageVisibility = ErrorMessageVisibility.Hidden;
        } else {
            // all other cases are invalid
            if (zipcodeConfirmButton) {
                zipcodeConfirmButton.disabled = true;
            }

            this.htmx.removeClass(zipcodeInput, 'is-valid');
            this.htmx.addClass(zipcodeInput, 'is-invalid');

            this.errorMessageVisibility = ErrorMessageVisibility.Visible;
        }
    }

    /**
     * Check if zipcode has valid format
     * @param zipcode - Zipcode to validate
     * @returns True if zipcode has a valid format
     */
    private isValidZipcodeFormat(zipcode: string): boolean {
        // First, check if isValidZipcode function exists in the window object
        const isValidZipcode = (window as any)['isValidZipcode'];

        if (typeof isValidZipcode === 'function') {
            return isValidZipcode(zipcode);
        }

        // Fallback validation if the global function isn't available
        // Accepts: "12345", "12345-1234", or "123451234"
        return /^\d{5}(-?\d{4})?$/.test(zipcode.trim());
    }
}
